import React, { useState, useContext, useEffect, useCallback } from 'react';
import {
  Dialog, Box, Typography, IconButton, TextField, Button, Tooltip, Chip, Checkbox,
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { makeStyles } from '@material-ui/core/styles';
import { useSelector, useDispatch } from 'react-redux';
import AddIcon from '@material-ui/icons/Add';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import PrintIcon from '@material-ui/icons/Print';
import CloseIcon from '@material-ui/icons/Close';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import SearchIcon from '@material-ui/icons/Search';
import SecurityIcon from '@material-ui/icons/Security';
import HotelIcon from '@material-ui/icons/Hotel';
import RoomIcon from '@material-ui/icons/Room';
import LocalGasStationIcon from '@material-ui/icons/LocalGasStation';
import StarIcon from '@material-ui/icons/Star';
import StarBorderIcon from '@material-ui/icons/StarBorder';
import KingBedIcon from '@material-ui/icons/KingBed';
import HomeWorkIcon from '@material-ui/icons/HomeWork';
import PlaceIcon from '@material-ui/icons/Place';
import CheckIcon from '@material-ui/icons/Check';
import LocalOfferIcon from '@material-ui/icons/LocalOffer';
import SettingsIcon from '@material-ui/icons/Settings';
import MyLocationIcon from '@material-ui/icons/MyLocation';
import { AuthContext } from '../../App';
import { selectLocations, fetchLocations, createLocation, updateLocation, deleteLocation } from '../../store/slices/locationsSlice';
import { navigateTo } from '../../store/slices/uiSlice';
import { addToast } from '../../store/slices/notificationsSlice';
import { t } from '../../i18n';
import { geocodeSearchMulti } from '../../utils/mapboxApi';

const TYPES = [
  { value: 'warehouse',  icon: HomeWorkIcon,        color: '#6366f1', bg: '#eef2ff', labelKey: 'typeWarehouse' },
  { value: 'safe_point', icon: SecurityIcon,        color: '#22c55e', bg: '#f0fdf4', labelKey: 'markerSafe' },
  { value: 'overnight',  icon: HotelIcon,           color: '#8b5cf6', bg: '#faf5ff', labelKey: 'markerOvernight' },
  { value: 'fuel',       icon: LocalGasStationIcon, color: '#ef4444', bg: '#fef2f2', labelKey: 'markerFuel' },
  { value: 'poi',        icon: RoomIcon,            color: '#f59e0b', bg: '#fffbeb', labelKey: 'markerPoi' },
  { value: 'rest_area',  icon: KingBedIcon,         color: '#7c3aed', bg: '#faf5ff', labelKey: 'typeRestArea' },
  { value: 'garage',     icon: RoomIcon,            color: '#3b82f6', bg: '#eff6ff', labelKey: 'typeGarage' },
  { value: 'other',      icon: RoomIcon,            color: '#64748b', bg: '#f8fafc', labelKey: 'typeOther' },
];

const emptyForm = { name: '', type: 'poi', categoryId: '', latitude: '', longitude: '', address: '', notes: '', isPreferred: false, includeInRoute: false, isDeparture: false, obraId: '' };

// Paleta sugerida para cores de categoria (escolha rápida).
const CAT_PALETTE = ['#0369a1', '#4f46e5', '#d97706', '#0d9488', '#dc2626', '#7c3aed', '#16a34a', '#db2777', '#64748b', '#0891b2'];

// ─── Gestor de categorias de locais (criar/renomear/cor/apagar) ──────────
// CRUD por cliente via /planner-api/location-categories. Ao mudar, chama
// onChanged() para o pai recarregar categorias + favoritos (apagar uma
// categoria desassocia os favoritos que a usavam).
function CategoryManagerDialog({ open, onClose, token, categories, onChanged, isPt }) {
  const [newName, setNewName] = useState('');
  const [newColor, setNewColor] = useState(CAT_PALETTE[0]);
  const [edits, setEdits] = useState({});     // id → nome em edição
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const api = async (method, path, body) => {
    const r = await fetch(`/planner-api/location-categories${path}`, {
      method,
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: body ? JSON.stringify(body) : undefined,
    });
    if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e.error || `HTTP ${r.status}`); }
    return r.status === 204 ? null : r.json().catch(() => null);
  };

  const add = async () => {
    const name = newName.trim();
    if (!name) return;
    setBusy(true); setErr('');
    try { await api('POST', '', { name, color: newColor }); setNewName(''); setNewColor(CAT_PALETTE[0]); await onChanged(); }
    catch (e) { setErr(e.message); } finally { setBusy(false); }
  };
  const saveName = async (c) => {
    const name = (edits[c.id] ?? c.name).trim();
    if (!name || name === c.name) { setEdits(p => { const n = { ...p }; delete n[c.id]; return n; }); return; }
    setBusy(true); setErr('');
    try { await api('PUT', `/${c.id}`, { name }); setEdits(p => { const n = { ...p }; delete n[c.id]; return n; }); await onChanged(); }
    catch (e) { setErr(e.message); } finally { setBusy(false); }
  };
  const saveColor = async (c, color) => {
    setBusy(true); setErr('');
    try { await api('PUT', `/${c.id}`, { color }); await onChanged(); }
    catch (e) { setErr(e.message); } finally { setBusy(false); }
  };
  const remove = async (c) => {
    if (!window.confirm(isPt ? `Apagar a categoria "${c.name}"? Os favoritos ficam sem categoria.` : `Delete category "${c.name}"?`)) return;
    setBusy(true); setErr('');
    try { await api('DELETE', `/${c.id}`); await onChanged(); }
    catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <Box style={{ padding: '18px 22px' }}>
        <Box style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
          <Typography style={{ flex: 1, fontSize: '1rem', fontWeight: 800, color: '#0f172a' }}>
            {isPt ? 'Categorias de locais' : 'Location categories'}
          </Typography>
          <IconButton size="small" onClick={onClose}><CloseIcon style={{ fontSize: 18 }} /></IconButton>
        </Box>
        <Typography style={{ fontSize: '0.74rem', color: '#64748b', marginBottom: 14 }}>
          {isPt ? 'Cria a tua própria taxonomia de favoritos (ex.: Obra, Estaleiro). É só etiqueta/cor — não altera o cálculo de rotas.'
                : 'Create your own favourites taxonomy. Label/colour only.'}
        </Typography>

        {err && <Box style={{ padding: '8px 12px', background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b', borderRadius: 8, marginBottom: 12, fontSize: '0.74rem' }}>{err}</Box>}

        {/* Lista */}
        <Box style={{ maxHeight: 280, overflowY: 'auto', marginBottom: 14 }}>
          {categories.length === 0 && (
            <Typography style={{ fontSize: '0.78rem', color: '#94a3b8', textAlign: 'center', padding: '18px 0' }}>
              {isPt ? 'Ainda não há categorias.' : 'No categories yet.'}
            </Typography>
          )}
          {categories.map(c => (
            <Box key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 0', borderBottom: '1px solid #f1f5f9' }}>
              <input type="color" value={c.color} disabled={busy}
                onChange={e => saveColor(c, e.target.value)}
                title={isPt ? 'Mudar cor' : 'Change colour'}
                style={{ width: 26, height: 26, border: 'none', background: 'none', padding: 0, cursor: 'pointer', flexShrink: 0 }} />
              <TextField variant="outlined" size="small" fullWidth
                value={edits[c.id] ?? c.name}
                onChange={e => setEdits(p => ({ ...p, [c.id]: e.target.value }))}
                onBlur={() => saveName(c)}
                onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); }}
                InputProps={{ style: { fontSize: '0.8rem' } }} />
              <Tooltip title={isPt ? 'Apagar' : 'Delete'}>
                <span><IconButton size="small" disabled={busy} onClick={() => remove(c)}>
                  <DeleteIcon style={{ fontSize: 16, color: '#ef4444' }} />
                </IconButton></span>
              </Tooltip>
            </Box>
          ))}
        </Box>

        {/* Adicionar */}
        <Typography style={{ fontSize: '0.72rem', fontWeight: 700, color: '#475569', marginBottom: 6 }}>
          {isPt ? 'Nova categoria' : 'New category'}
        </Typography>
        <Box style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <input type="color" value={newColor} onChange={e => setNewColor(e.target.value)}
            style={{ width: 30, height: 30, border: 'none', background: 'none', padding: 0, cursor: 'pointer', flexShrink: 0 }} />
          <TextField variant="outlined" size="small" fullWidth
            placeholder={isPt ? 'Ex: Obra' : 'Ex: Site'}
            value={newName} onChange={e => setNewName(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') add(); }}
            InputProps={{ style: { fontSize: '0.8rem' } }} />
          <Button variant="contained" disableElevation disabled={busy || !newName.trim()} onClick={add}
            startIcon={<AddIcon />}
            style={{ background: newName.trim() ? '#4f46e5' : '#cbd5e1', color: '#fff', textTransform: 'none', fontWeight: 700, whiteSpace: 'nowrap' }}>
            {isPt ? 'Criar' : 'Add'}
          </Button>
        </Box>
        <Box style={{ display: 'flex', gap: 5, marginTop: 8 }}>
          {CAT_PALETTE.map(col => (
            <Box key={col} onClick={() => setNewColor(col)}
              style={{ width: 18, height: 18, borderRadius: '50%', background: col, cursor: 'pointer',
                border: newColor === col ? '2px solid #0f172a' : '2px solid transparent' }} />
          ))}
        </Box>
      </Box>
    </Dialog>
  );
}

const useStyles = makeStyles(() => ({
  root: {
    display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden',
    background: '#f8fafc',
  },
  header: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '16px 20px', background: 'linear-gradient(180deg, #ffffff 0%, #dbeafe 100%)',
    borderBottom: '1px solid #bfdbfe', flexShrink: 0,
  },
  headerTitle: {
    display: 'flex', alignItems: 'center', gap: 12,
  },
  headerIcon: {
    width: 38, height: 38, borderRadius: 10,
    background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    boxShadow: '0 2px 8px rgba(59,130,246,0.3)',
  },
  toolbar: {
    display: 'flex', gap: 8, alignItems: 'center',
  },
  searchField: {
    flex: 1,
    '& .MuiOutlinedInput-root': { borderRadius: 8, background: '#f8fafc', fontSize: '0.76rem', height: 36 },
    '& .MuiOutlinedInput-input': { padding: '8px 12px' },
  },
  filterBar: {
    display: 'flex', gap: 6, padding: '10px 20px', flexWrap: 'wrap', flexShrink: 0,
    background: '#fff', borderBottom: '1px solid #f1f5f9', alignItems: 'center',
  },
  chip: {
    cursor: 'pointer', fontSize: '0.72rem', fontWeight: 600,
    transition: 'all 0.15s',
  },
  grid: {
    flex: 1, overflow: 'auto', padding: 0, background: '#fff',
  },
  card: {
    display: 'flex', alignItems: 'center', gap: 10,
    padding: '8px 14px', borderBottom: '1px solid #f1f5f9',
    transition: 'background 0.1s', cursor: 'default',
    '&:hover': { background: '#f8fafc' },
  },
  cardHeader: {
    display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 10,
  },
  cardIcon: {
    width: 40, height: 40, borderRadius: 10,
    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
  },
  cardName: {
    fontSize: '0.8rem', fontWeight: 700, color: '#0f172a',
    overflow: 'hidden', textOverflow: 'ellipsis',
    display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
  },
  cardAddress: {
    fontSize: '0.65rem', color: '#64748b', marginTop: 3,
    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
  },
  cardCoords: {
    fontSize: '0.58rem', color: '#94a3b8', marginTop: 1,
  },
  cardFooter: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    marginTop: 'auto', paddingTop: 10, borderTop: '1px solid #f1f5f9',
  },
  cardActions: {
    display: 'flex', gap: 1,
  },
  formOverlay: {
    position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 10,
    background: '#fff',
    display: 'flex', flexDirection: 'column', overflow: 'hidden',
  },
  formHeader: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '16px 24px',
    background: 'linear-gradient(180deg, #ffffff 0%, #dbeafe 100%)',
    borderBottom: '1px solid #bfdbfe', flexShrink: 0,
  },
  formBody: {
    flex: 1, overflow: 'auto', padding: '24px 28px', background: '#f8fafc',
  },
  formLabel: {
    fontSize: '0.62rem', fontWeight: 700, color: '#475569',
    textTransform: 'uppercase', letterSpacing: '0.06em',
    marginBottom: 8, marginTop: 20,
  },
  formInput: {
    '& .MuiOutlinedInput-root': { borderRadius: 10, background: '#fff', fontSize: '0.8rem', border: '1px solid #e2e8f0' },
    '& .MuiOutlinedInput-input': { padding: '11px 14px' },
    '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
    '& .MuiOutlinedInput-root:hover': { borderColor: '#93c5fd' },
    '& .MuiOutlinedInput-root.Mui-focused': { borderColor: '#3b82f6', boxShadow: '0 0 0 3px rgba(59,130,246,0.1)' },
  },
  typeGrid: {
    display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10,
  },
  typeBtn: {
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6,
    padding: '14px 8px', borderRadius: 14, cursor: 'pointer',
    transition: 'all 0.2s', border: '2px solid transparent',
    background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
    '&:hover': { background: '#f0f9ff', transform: 'translateY(-1px)', boxShadow: '0 3px 8px rgba(0,0,0,0.08)' },
  },
  coordRow: { display: 'flex', gap: 10, alignItems: 'flex-end' },
  prefRow: {
    display: 'flex', alignItems: 'center', gap: 12, padding: '14px 18px',
    borderRadius: 14, cursor: 'pointer', marginTop: 20, transition: 'all 0.2s',
    background: '#fff', border: '1px solid #e2e8f0',
    '&:hover': { borderColor: '#93c5fd', background: '#f0f9ff' },
  },
  formActions: {
    display: 'flex', gap: 10, justifyContent: 'flex-end', padding: '16px 28px',
    borderTop: '1px solid #e2e8f0', flexShrink: 0, background: '#fff',
  },
  deleteOverlay: {
    position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 20,
    background: 'rgba(15,23,42,0.5)', backdropFilter: 'blur(3px)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  empty: {
    gridColumn: '1 / -1', textAlign: 'center', padding: '48px 20px',
  },
  pickMapBanner: {
    display: 'flex', alignItems: 'center', gap: 8, padding: '10px 24px',
    background: '#eff6ff', borderBottom: '1px solid #bfdbfe', flexShrink: 0,
  },
}));

function ManageFavoritesDialog({ open, onClose, embedded, onPickFromMap, onBack }) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { token, clientSettings, clientName } = useContext(AuthContext);
  const locations = useSelector(selectLocations);

  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all'); // 'all' | 'none' | <categoryId>
  const [importingGeo, setImportingGeo] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ ...emptyForm });
  const [editId, setEditId] = useState(null);
  const [error, setError] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  // Geocoding pormenorizado do campo de morada (#8) — sugestões à
  // medida que se escreve, escolha preenche morada + coordenadas.
  const [addrSuggestions, setAddrSuggestions] = useState([]);
  const [addrFocused, setAddrFocused] = useState(false);
  const addrDebounceRef = React.useRef(null);
  const [pickingFromMap, setPickingFromMap] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [minimized, setMinimized] = useState(false);
  const [page, setPage] = useState(1);
  const [filterCountry, setFilterCountry] = useState('all');
  // Ordenação da tabela: coluna + direção (clicar no cabeçalho alterna ASC/DESC).
  const [sortBy, setSortBy] = useState('name'); // 'name' | 'address' | 'type' | 'coords'
  const [sortDir, setSortDir] = useState('asc'); // 'asc' | 'desc'
  const toggleSort = (col) => {
    if (sortBy === col) { setSortDir(d => (d === 'asc' ? 'desc' : 'asc')); }
    else { setSortBy(col); setSortDir('asc'); }
    setPage(1);
  };
  const ITEMS_PER_PAGE = 20;
  const [hoveredCardId, setHoveredCardId] = useState(null);
  const [cardPhotos, setCardPhotos] = useState({}); // { locId: photoUrl }
  // Categorias de locais (dimensão por-cliente, separada do tipo funcional).
  const [categories, setCategories] = useState([]);
  const [showCatManager, setShowCatManager] = useState(false);
  // Obras do cliente (Casais) — para ligar um favorito a uma obra. Lista vazia
  // nos clientes sem obras → o controlo de ligação nem aparece.
  const [obrasList, setObrasList] = useState([]);
  // Criar obra a partir deste favorito (pede o código da obra).
  const [obraCreate, setObraCreate] = useState({ show: false, codigo: '', saving: false, err: '' });
  // Criação inline de categoria (dentro do form do favorito) — mais intuitivo
  // que ter de abrir o gestor só para criar uma.
  const [inlineCat, setInlineCat] = useState({ show: false, name: '', color: CAT_PALETTE[0] });

  const lang = 'pt';
  const isPt = lang === 'pt';

  const fetchCategories = useCallback(async () => {
    if (!token) return;
    try {
      const r = await fetch('/planner-api/location-categories', { headers: { Authorization: `Bearer ${token}` } });
      if (r.ok) setCategories(await r.json());
    } catch { /* silencioso — categorias são opcionais */ }
  }, [token]);

  const fetchObras = useCallback(async () => {
    if (!token) return;
    try {
      const r = await fetch('/planner-api/obras', { headers: { Authorization: `Bearer ${token}` } });
      if (r.ok) { const d = await r.json(); setObrasList(Array.isArray(d) ? d : []); }
    } catch { /* silencioso — só existem obras na variante mercadoria */ }
  }, [token]);

  // Cria uma obra com os dados deste favorito (nome + coords) + código introduzido,
  // e liga-a (form.obraId). O favorito é gravado depois com esse obraId.
  async function createObraFromFavorite() {
    const codigo = obraCreate.codigo.trim();
    if (!codigo) { setObraCreate(s => ({ ...s, err: isPt ? 'Código obrigatório' : 'Code required' })); return; }
    setObraCreate(s => ({ ...s, saving: true, err: '' }));
    try {
      const r = await fetch('/planner-api/obras', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          codigoObra: codigo,
          name: form.name || null,
          lat: form.latitude ? Number(form.latitude) : null,
          lng: form.longitude ? Number(form.longitude) : null,
          // Liga já este favorito (quando é um já gravado) → fica ligado mesmo
          // que o utilizador não grave logo o favorito. Tipo passa a "outros".
          locationId: editId || undefined,
        }),
      });
      if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e.error || `HTTP ${r.status}`); }
      const obra = await r.json();
      await fetchObras();
      upd('obraId', obra.id);
      upd('type', 'other'); // favorito de obra = tipo "outros"
      if (editId) dispatch(fetchLocations(token)); // reflete o link imediato
      setObraCreate({ show: false, codigo: '', saving: false, err: '' });
    } catch (e) {
      setObraCreate(s => ({ ...s, saving: false, err: e.message || 'Erro' }));
    }
  }

  async function createInlineCategory() {
    const name = inlineCat.name.trim();
    if (!name) return;
    try {
      const r = await fetch('/planner-api/location-categories', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, color: inlineCat.color }),
      });
      if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e.error || `HTTP ${r.status}`); }
      const created = await r.json();
      await fetchCategories();
      upd('categoryId', created.id);     // seleciona logo a nova categoria
      setInlineCat({ show: false, name: '', color: CAT_PALETTE[0] });
      setError('');
    } catch (e) { setError(e.message); }
  }

  useEffect(() => {
    if (open && token && locations.length === 0) {
      dispatch(fetchLocations(token));
    }
    if (open) { setMinimized(false); fetchCategories(); fetchObras(); }
  }, [open, token, locations.length, dispatch, fetchCategories, fetchObras]);

  // Importar geofences do EcooGPS como favoritos (só tenants com a flag).
  const handleImportGeofences = async () => {
    setImportingGeo(true);
    try {
      const r = await fetch('/planner-api/locations/import-geofences', {
        method: 'POST', headers: { Authorization: `Bearer ${token}` },
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'erro');
      dispatch(fetchLocations(token));
      const detail = isPt
        ? `${d.imported} nova(s), ${d.skipped} já existia(m)${d.ignoredLines ? `, ${d.ignoredLines} linha(s) ignorada(s)` : ''}.`
        : `${d.imported} new, ${d.skipped} already existed${d.ignoredLines ? `, ${d.ignoredLines} line(s) skipped` : ''}.`;
      dispatch(addToast({ type: 'info', severity: d.imported > 0 ? 'success' : 'info',
        message: isPt ? 'Geofences importadas' : 'Geofences imported', detail }));
    } catch (e) {
      dispatch(addToast({ type: 'info', severity: 'warning',
        message: isPt ? 'Não consegui importar as geofences' : 'Could not import geofences', detail: e.message }));
    }
    setImportingGeo(false);
  };

  // Listen for "open add new with pre-filled coords" from map.
  // Aceita um `name` explícito (ex: "Estádio do Benfica" vindo da
  // pesquisa do Google Places) e cai para a primeira parte da morada
  // só se o name não vier preenchido.
  useEffect(() => {
    function handleOpenAddNew(e) {
      const { lat, lng, address, name } = e.detail || {};
      if (lat == null || lng == null) return;
      setEditId(null);
      const resolvedName = (name && String(name).trim())
        || (address ? String(address).split(',')[0].trim() : '');
      setForm({
        ...emptyForm,
        latitude: Number(lat).toFixed(7),
        longitude: Number(lng).toFixed(7),
        address: address || '',
        name: resolvedName,
      });
      setShowForm(true);
      setError('');
    }
    // "Editar favorito" vindo do popup do mapa — abre direto o formulário de
    // edição desse favorito (startEdit é hoisted, por isso está acessível aqui).
    function handleOpenEdit(e) {
      const loc = e.detail?.loc;
      if (loc && (loc.id != null)) startEdit(loc);
    }
    window.addEventListener('favoritesOpenAddNew', handleOpenAddNew);
    window.addEventListener('favoritesOpenEdit', handleOpenEdit);
    return () => {
      window.removeEventListener('favoritesOpenAddNew', handleOpenAddNew);
      window.removeEventListener('favoritesOpenEdit', handleOpenEdit);
    };
  }, []);

  function changeFilter(val) { setFilterType(val); setPage(1); setFilterCountry('all'); }
  function upd(key, val) { setForm(f => ({ ...f, [key]: val })); }

  function resetForm() {
    setForm({ ...emptyForm }); setShowForm(false);
    setEditId(null); setError(''); setPickingFromMap(false);
    setInlineCat({ show: false, name: '', color: CAT_PALETTE[0] });
    setObraCreate({ show: false, codigo: '', saving: false, err: '' });
  }

  function openNewForm() {
    setEditId(null); setForm({ ...emptyForm }); setShowForm(true); setError('');
    setInlineCat({ show: false, name: '', color: CAT_PALETTE[0] });
    setObraCreate({ show: false, codigo: '', saving: false, err: '' });
  }

  function startEdit(loc) {
    setEditId(loc.id);
    setForm({
      name: loc.name || '', type: loc.type || 'poi',
      categoryId: loc.categoryId || '',
      latitude: loc.latitude || '', longitude: loc.longitude || '',
      address: loc.address || '', notes: loc.notes || '',
      isPreferred: loc.isPreferred || false,
      includeInRoute: loc.includeInRoute || false,
      isDeparture: loc.isDeparture || false,
      obraId: loc.obraId || '',
    });
    setShowForm(true); setError('');
    setInlineCat({ show: false, name: '', color: CAT_PALETTE[0] });
    setObraCreate({ show: false, codigo: '', saving: false, err: '' });
  }

  async function handleSave() {
    if (!form.name.trim()) return setError(isPt ? 'Nome obrigatório' : 'Name required');
    if (!form.latitude || !form.longitude) return setError(isPt ? 'Coordenadas obrigatórias' : 'Coordinates required');
    try {
      const lat = Number(form.latitude);
      const lng = Number(form.longitude);
      // Coerência: a ligação à obra vive com a categoria "Obra". Se a categoria
      // não for "Obra", o favorito fica sem obra (desliga).
      const selCat = categories.find(c => Number(c.id) === Number(form.categoryId));
      const isObraCat = !!selCat && /^obra$/i.test((selCat.name || '').trim());
      const payload = { ...form, latitude: lat, longitude: lng, obraId: isObraCat ? (form.obraId || null) : null };
      if (editId) {
        await dispatch(updateLocation({ token, id: editId, data: payload })).unwrap();
      } else {
        await dispatch(createLocation({ token, location: payload })).unwrap();
        window.dispatchEvent(new CustomEvent('favoriteAdded', { detail: { lat, lng, type: form.type } }));
      }
      await fetchObras(); // reflete a ligação/nova obra nos selos
      resetForm();
    } catch (err) { setError(err.message); }
  }

  async function handleDelete(id) {
    try { await dispatch(deleteLocation({ token, id })).unwrap(); } catch (err) { setError(err.message); }
  }

  // Handle map pick callback
  const handleMapPick = useCallback((lat, lng, address) => {
    upd('latitude', lat.toFixed(7));
    upd('longitude', lng.toFixed(7));
    if (address) upd('address', address);
    setPickingFromMap(false);
  }, []);

  // Photo fetch removed — was causing map to disappear on hover

  function toggleSelect(id) {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }
  function selectAll() {
    if (selectedIds.size === filtered.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(filtered.map(l => l.id)));
  }
  function handleShowOnMap() {
    const locs = locations.filter(l => selectedIds.has(l.id));
    window.dispatchEvent(new CustomEvent('showLocationsOnMap', { detail: locs }));
    // Navigate to the planner keeping "favorites" as previousView so the back button appears
    dispatch(navigateTo('planner'));
    if (onClose) onClose();
  }

  // Imprimir a Lista de Favoritos em PDF, agrupada por tipo (pernoita,
  // ponto seguro, área de descanso, etc.). Respeita o filtro/pesquisa actual:
  // se houver selecção, imprime só os seleccionados; senão os filtrados.
  function handlePrintFavorites() {
    import('jspdf').then(({ default: jsPDF }) => {
      const doc = new jsPDF({ unit: 'mm', format: 'a4' });
      const source = selectedIds.size > 0
        ? locations.filter(l => selectedIds.has(l.id))
        : filtered;
      const pageW = doc.internal.pageSize.getWidth();
      const pageH = doc.internal.pageSize.getHeight();
      let y = 18;
      doc.setFontSize(15); doc.setTextColor(30, 41, 59);
      doc.text(isPt ? 'Lista de Favoritos' : 'Favourites List', 14, y);
      y += 6;
      doc.setFontSize(9); doc.setTextColor(120, 120, 120);
      doc.text(`${clientName || ''}${clientName ? ' · ' : ''}${new Date().toLocaleString(isPt ? 'pt-PT' : 'en-GB')} · ${source.length} ${isPt ? 'locais' : 'places'}`, 14, y);
      y += 8;

      const ensureSpace = (needed) => {
        if (y + needed > pageH - 14) { doc.addPage(); y = 18; }
      };

      TYPES.forEach(tp => {
        const group = source.filter(l => l.type === tp.value);
        if (group.length === 0) return;
        ensureSpace(14);
        // Cabeçalho da secção do tipo
        const [r, g, b] = hexToRgb(tp.color);
        doc.setFillColor(r, g, b);
        doc.rect(14, y - 4, 3, 6, 'F');
        doc.setFontSize(11); doc.setTextColor(30, 41, 59);
        doc.text(`${t(lang, tp.labelKey)}  (${group.length})`, 20, y);
        y += 7;
        doc.setFontSize(9);
        group.forEach((loc, i) => {
          ensureSpace(10);
          doc.setTextColor(15, 23, 42);
          const star = loc.isPreferred ? '* ' : '';
          doc.text(`${i + 1}. ${star}${loc.name || '—'}`, 20, y);
          y += 4.5;
          if (loc.address) {
            doc.setTextColor(120, 120, 120);
            const addr = doc.splitTextToSize(loc.address, pageW - 40);
            doc.text(addr, 24, y);
            y += 4.5 * addr.length;
          }
          if (loc.notes) {
            doc.setTextColor(150, 150, 150);
            const nt = doc.splitTextToSize(`${isPt ? 'Nota' : 'Note'}: ${loc.notes}`, pageW - 40);
            doc.text(nt, 24, y);
            y += 4.5 * nt.length;
          }
          y += 1.5;
        });
        y += 4;
      });

      doc.save(`favoritos_${new Date().toISOString().slice(0, 10)}.pdf`);
    });
  }

  // #RRGGBB → [r,g,b]
  function hexToRgb(hex) {
    const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex || '');
    return m ? [parseInt(m[1], 16), parseInt(m[2], 16), parseInt(m[3], 16)] : [100, 116, 139];
  }

  // Detect country from address or notes
  function detectCountry(loc) {
    const addr = ((loc.address || '') + ' ' + (loc.notes || '')).toLowerCase();
    if (addr.includes('portugal') || addr.includes(', pt') || addr.includes(', por')) return 'Portugal';
    if (addr.includes('espanha') || addr.includes('españa') || addr.includes('spain') || addr.includes(', es')) return 'Espanha';
    if (addr.includes('frança') || addr.includes('france') || addr.includes(', fr')) return 'França';
    if (addr.includes('bélgica') || addr.includes('belgium') || addr.includes(', be')) return 'Bélgica';
    if (addr.includes('alemanha') || addr.includes('germany') || addr.includes(', de')) return 'Alemanha';
    if (addr.includes('itália') || addr.includes('italy') || addr.includes(', it')) return 'Itália';
    if (addr.includes('holanda') || addr.includes('netherlands') || addr.includes(', nl')) return 'Holanda';
    // Detect by network in notes
    const net = (loc.notes || '').toLowerCase();
    if (net.includes('prio')) return 'Portugal';
    if (net.includes('repsol')) return 'Espanha';
    if (net.includes('shell') && net.includes('fr')) return 'França';
    return null;
  }

  const filtered = locations.filter(loc => {
    if (filterType !== 'all' && loc.type !== filterType) return false;
    if (filterCategory === 'none' && loc.categoryId) return false;
    if (filterCategory !== 'all' && filterCategory !== 'none' && Number(loc.categoryId) !== Number(filterCategory)) return false;
    if (filterCountry !== 'all') {
      const country = detectCountry(loc);
      if (country !== filterCountry) return false;
    }
    if (search.length >= 2) {
      const q = search.toLowerCase();
      return (loc.name || '').toLowerCase().includes(q) || (loc.address || '').toLowerCase().includes(q);
    }
    return true;
  });

  // Ordenação (ASC/DESC) por coluna. Texto: localeCompare PT sem acentos/caixa.
  // Coordenadas: por latitude e depois longitude (numérico).
  const sorted = React.useMemo(() => {
    const arr = [...filtered];
    const txt = (v) => String(v || '');
    const cmp = (a, b) => {
      if (sortBy === 'coords') {
        const la = Number(a.latitude), lb = Number(b.latitude);
        const na = Number.isFinite(la), nb = Number.isFinite(lb);
        if (na && nb && la !== lb) return la - lb;
        if (na !== nb) return na ? -1 : 1;
        return (Number(a.longitude) || 0) - (Number(b.longitude) || 0);
      }
      if (sortBy === 'type') {
        const ta = TYPES.find(t => t.value === a.type)?.value || 'zzz';
        const tb = TYPES.find(t => t.value === b.type)?.value || 'zzz';
        return txt(ta).localeCompare(txt(tb), 'pt', { sensitivity: 'base' });
      }
      const key = sortBy === 'address' ? 'address' : 'name';
      return txt(a[key]).localeCompare(txt(b[key]), 'pt', { sensitivity: 'base' });
    };
    arr.sort(cmp);
    if (sortDir === 'desc') arr.reverse();
    return arr;
  }, [filtered, sortBy, sortDir]);

  const typeCounts = {};
  locations.forEach(l => { typeCounts[l.type] = (typeCounts[l.type] || 0) + 1; });

  // Contagem por categoria (para os chips de filtro) + sem categoria.
  const catCounts = {};
  let noCatCount = 0;
  locations.forEach(l => { if (l.categoryId) catCounts[l.categoryId] = (catCounts[l.categoryId] || 0) + 1; else noCatCount++; });

  // Country counts (only for fuel type)
  const countryCounts = {};
  if (filterType === 'fuel') {
    locations.filter(l => l.type === 'fuel').forEach(l => {
      const c = detectCountry(l) || 'Outro';
      countryCounts[c] = (countryCounts[c] || 0) + 1;
    });
  }

  const content = (
    <Box className={classes.root}>
      {/* Header — fullscreen style like Orders */}
      <Box style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 24px', background: '#fff', borderBottom: '1px solid #e2e8f0', flexShrink: 0 }}>
        {(!embedded || onBack) && (
          <IconButton size="small" onClick={onBack || onClose} style={{ color: '#64748b' }}>
            <ArrowBackIcon style={{ fontSize: 20 }} />
          </IconButton>
        )}
        <StarIcon style={{ fontSize: 22, color: '#f59e0b' }} />
        <Box style={{ flex: 1 }}>
          <Box style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Typography style={{ fontSize: '1.1rem', fontWeight: 700, color: '#1e293b' }}>
              {isPt ? 'Favoritos' : 'Favourites'}
            </Typography>
            <Chip label={locations.length} size="small" style={{ height: 22, fontSize: '0.68rem', fontWeight: 700, background: '#fef3c7', color: '#f59e0b' }} />
          </Box>
        </Box>
        <TextField variant="outlined" size="small"
          placeholder={isPt ? 'Pesquisar locais...' : 'Search locations...'}
          value={search} onChange={e => setSearch(e.target.value)}
          style={{ width: 260 }}
          InputProps={{
            startAdornment: <SearchIcon style={{ fontSize: 16, color: '#94a3b8', marginRight: 6 }} />,
            style: { fontSize: '0.78rem', height: 36, borderRadius: 10 },
          }}
        />
        <Box style={{ display: 'flex', gap: 8 }}>
          {selectedIds.size > 0 && (
            <Button size="small" variant="outlined"
              startIcon={<PlaceIcon style={{ fontSize: 14 }} />}
              onClick={handleShowOnMap}
              style={{ fontSize: '0.72rem', textTransform: 'none', fontWeight: 600, borderRadius: 10, borderColor: '#22c55e', color: '#22c55e', padding: '6px 16px' }}>
              {isPt ? `Ver no Mapa (${selectedIds.size})` : `Show on Map (${selectedIds.size})`}
            </Button>
          )}
          <Tooltip title={isPt ? 'Imprimir lista de favoritos (PDF)' : 'Print favourites list (PDF)'}>
            <Button size="small" variant="outlined"
              startIcon={<PrintIcon style={{ fontSize: 15 }} />}
              onClick={handlePrintFavorites}
              disabled={locations.length === 0}
              style={{ fontSize: '0.72rem', textTransform: 'none', fontWeight: 600, borderRadius: 10, borderColor: '#cbd5e1', color: '#475569', padding: '6px 16px' }}>
              {isPt ? 'Imprimir' : 'Print'}
            </Button>
          </Tooltip>
          {clientSettings?.geofenceFavorites && (
            <Tooltip title={isPt ? 'Importar as geofences da tua conta EcooGPS como favoritos' : 'Import your EcooGPS account geofences as favourites'}>
              <span>
                <Button size="small" variant="outlined"
                  startIcon={<MyLocationIcon style={{ fontSize: 15 }} />}
                  onClick={handleImportGeofences}
                  disabled={importingGeo}
                  style={{ fontSize: '0.72rem', textTransform: 'none', fontWeight: 600, borderRadius: 10, borderColor: '#6366f1', color: '#4338ca', padding: '6px 16px' }}>
                  {importingGeo ? (isPt ? 'A importar…' : 'Importing…') : (isPt ? 'Importar geofences' : 'Import geofences')}
                </Button>
              </span>
            </Tooltip>
          )}
          <Button size="small" variant="contained"
            startIcon={<AddIcon style={{ fontSize: 16 }} />}
            onClick={openNewForm}
            style={{ fontSize: '0.72rem', textTransform: 'none', fontWeight: 700, borderRadius: 10, background: 'linear-gradient(135deg, #22c55e, #16a34a)', color: '#fff', padding: '6px 20px', boxShadow: '0 2px 8px rgba(34,197,94,0.3)' }}>
            {isPt ? '+ Novo Favorito' : '+ New Favourite'}
          </Button>
        </Box>
      </Box>

      {/* Filter chips + select all */}
      <Box className={classes.filterBar}>
        <Tooltip title={isPt ? 'Seleccionar todos' : 'Select all'}>
          <Checkbox size="small" style={{ padding: 2, marginRight: 2 }}
            checked={selectedIds.size > 0 && selectedIds.size === filtered.length}
            indeterminate={selectedIds.size > 0 && selectedIds.size < filtered.length}
            onChange={selectAll} />
        </Tooltip>
        <Chip label={`${isPt ? 'Todos' : 'All'} (${locations.length})`} size="small"
          className={classes.chip}
          onClick={() => changeFilter('all')}
          style={filterType === 'all' ? { background: '#1e293b', color: '#fff' } : { background: '#fff', border: '1px solid #e2e8f0' }} />
        {TYPES.map(tp => {
          const count = typeCounts[tp.value] || 0;
          if (count === 0) return null;
          const TIcon = tp.icon;
          return (
            <Chip key={tp.value} size="small" className={classes.chip}
              icon={<TIcon style={{ fontSize: 12, color: filterType === tp.value ? '#fff' : tp.color }} />}
              label={`${t(lang, tp.labelKey)} (${count})`}
              onClick={() => changeFilter(filterType === tp.value ? 'all' : tp.value)}
              style={filterType === tp.value
                ? { background: tp.color, color: '#fff' }
                : { background: '#fff', border: '1px solid #e2e8f0' }} />
          );
        })}
        {/* Category filter chips (dimensão do cliente) */}
        {categories.length > 0 && (<>
          <Box style={{ display: 'flex', alignItems: 'center', gap: 6, margin: '0 2px 0 6px' }}>
            <Box style={{ width: 1, height: 20, background: '#e2e8f0' }} />
            <Typography style={{ fontSize: '0.56rem', fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              {isPt ? 'Categoria' : 'Category'}
            </Typography>
          </Box>
          {categories.filter(c => (catCounts[c.id] || 0) > 0).map(c => (
            <Chip key={`cat-${c.id}`} size="small" className={classes.chip}
              avatar={<Box style={{ width: 9, height: 9, borderRadius: '50%', background: filterCategory === c.id ? '#fff' : c.color, margin: '0 0 0 8px' }} />}
              label={`${c.name} (${catCounts[c.id]})`}
              onClick={() => { setFilterCategory(filterCategory === c.id ? 'all' : c.id); setPage(1); }}
              style={filterCategory === c.id
                ? { background: c.color, color: '#fff' }
                : { background: '#fff', border: '1px solid #e2e8f0' }} />
          ))}
          {noCatCount > 0 && (
            <Chip size="small" className={classes.chip}
              label={`${isPt ? 'Sem categoria' : 'No category'} (${noCatCount})`}
              onClick={() => { setFilterCategory(filterCategory === 'none' ? 'all' : 'none'); setPage(1); }}
              style={filterCategory === 'none'
                ? { background: '#64748b', color: '#fff' }
                : { background: '#fff', border: '1px solid #e2e8f0' }} />
          )}
        </>)}
        {/* Country filter chips — visible when fuel type selected */}
        {filterType === 'fuel' && Object.keys(countryCounts).length > 1 && (<>
          <Box style={{ width: 1, height: 20, background: '#e2e8f0', margin: '0 4px' }} />
          {Object.entries(countryCounts).sort((a, b) => b[1] - a[1]).map(([country, count]) => {
            const countryFlags = { Portugal: '🇵🇹', Espanha: '🇪🇸', França: '🇫🇷', Bélgica: '🇧🇪', Alemanha: '🇩🇪', Itália: '🇮🇹', Holanda: '🇳🇱', Outro: '🌍' };
            return (
              <Chip key={country} size="small" className={classes.chip}
                label={`${countryFlags[country] || '🌍'} ${country} (${count})`}
                onClick={() => { setFilterCountry(filterCountry === country ? 'all' : country); setPage(1); }}
                style={filterCountry === country
                  ? { background: '#1e40af', color: '#fff' }
                  : { background: '#fff', border: '1px solid #e2e8f0' }} />
            );
          })}
        </>)}
      </Box>

      {/* Pagination info */}
      {filtered.length > 0 && (
        <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 14px', background: '#f8fafc', borderBottom: '1px solid #f1f5f9', flexShrink: 0 }}>
          <Typography style={{ fontSize: '0.62rem', color: '#94a3b8' }}>
            {((page - 1) * ITEMS_PER_PAGE) + 1}-{Math.min(page * ITEMS_PER_PAGE, filtered.length)} {isPt ? 'de' : 'of'} {filtered.length}
          </Typography>
          <Box style={{ display: 'flex', gap: 4 }}>
            <Button size="small" disabled={page <= 1} onClick={() => setPage(p => p - 1)}
              style={{ fontSize: '0.58rem', minWidth: 0, padding: '2px 8px', textTransform: 'none' }}>
              ←
            </Button>
            <Typography style={{ fontSize: '0.62rem', color: '#64748b', lineHeight: '24px' }}>
              {page}/{Math.ceil(filtered.length / ITEMS_PER_PAGE)}
            </Typography>
            <Button size="small" disabled={page >= Math.ceil(filtered.length / ITEMS_PER_PAGE)} onClick={() => setPage(p => p + 1)}
              style={{ fontSize: '0.58rem', minWidth: 0, padding: '2px 8px', textTransform: 'none' }}>
              →
            </Button>
          </Box>
        </Box>
      )}

      {/* ═══ Table view — modern, dense, sortable rows ═══ */}
      <Box className={classes.grid} style={{ padding: 0 }}>
        {filtered.length === 0 && !showForm && (
          <Box style={{ textAlign: 'center', padding: '60px 20px' }}>
            <Box style={{
              width: 64, height: 64, borderRadius: 16,
              background: 'linear-gradient(135deg, #f1f5f9, #e2e8f0)',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              marginBottom: 12,
            }}>
              <PlaceIcon style={{ fontSize: 30, color: '#cbd5e1' }} />
            </Box>
            <Typography style={{ fontSize: '0.92rem', fontWeight: 700, color: '#475569', marginBottom: 4 }}>
              {search.length >= 2 ? (isPt ? 'Nenhum resultado' : 'No results') : (isPt ? 'Sem locais guardados' : 'No saved locations')}
            </Typography>
            <Typography style={{ fontSize: '0.72rem', color: '#94a3b8' }}>
              {search.length >= 2
                ? (isPt ? 'Tenta pesquisar por outro nome ou morada' : 'Try another name or address')
                : (isPt ? 'Clica em "+ Novo Favorito" para criar o primeiro' : 'Click "+ New Favourite" to create one')}
            </Typography>
          </Box>
        )}

        {filtered.length > 0 && (
          <Box style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            {/* Sticky column header */}
            <Box style={{
              display: 'grid',
              gridTemplateColumns: '40px 32px 2.1fr 2.2fr 0.95fr 1fr 110px 108px',
              gap: 0, alignItems: 'center',
              padding: '8px 16px',
              background: '#f8fafc', borderBottom: '1px solid #e2e8f0',
              position: 'sticky', top: 0, zIndex: 2,
              fontSize: '0.6rem', fontWeight: 800, color: '#64748b',
              textTransform: 'uppercase', letterSpacing: '0.05em',
            }}>
              <Checkbox size="small"
                checked={selectedIds.size > 0 && selectedIds.size === filtered.length}
                indeterminate={selectedIds.size > 0 && selectedIds.size < filtered.length}
                onChange={selectAll}
                style={{ padding: 2, color: '#cbd5e1' }} />
              <Box style={{ textAlign: 'center' }}>
                <StarBorderIcon style={{ fontSize: 13, color: '#94a3b8' }} />
              </Box>
              {(() => {
                const arrow = (col) => (sortBy === col ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '');
                const hStyle = (col) => ({ cursor: 'pointer', userSelect: 'none', color: sortBy === col ? '#4338ca' : '#64748b' });
                return (
                  <>
                    <Box onClick={() => toggleSort('name')} style={hStyle('name')} title={isPt ? 'Ordenar por nome' : 'Sort by name'}>{isPt ? 'Nome' : 'Name'}{arrow('name')}</Box>
                    <Box onClick={() => toggleSort('address')} style={hStyle('address')} title={isPt ? 'Ordenar por morada' : 'Sort by address'}>{isPt ? 'Morada' : 'Address'}{arrow('address')}</Box>
                    <Box onClick={() => toggleSort('type')} style={hStyle('type')} title={isPt ? 'Ordenar por tipo' : 'Sort by type'}>{isPt ? 'Tipo' : 'Type'}{arrow('type')}</Box>
                    <Box style={{ color: '#64748b' }}>{isPt ? 'Categoria' : 'Category'}</Box>
                    <Box onClick={() => toggleSort('coords')} style={hStyle('coords')} title={isPt ? 'Ordenar por coordenadas' : 'Sort by coordinates'}>{isPt ? 'Coordenadas' : 'Coordinates'}{arrow('coords')}</Box>
                  </>
                );
              })()}
              <Box style={{ textAlign: 'right' }}>{isPt ? 'Ações' : 'Actions'}</Box>
            </Box>

            {/* Rows */}
            <Box style={{ flex: 1, overflow: 'auto', background: '#fff' }}>
              {sorted.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE).map((loc, idx) => {
                const tp = TYPES.find(t => t.value === loc.type) || TYPES[7];
                const TIcon = tp.icon;
                const isSelected = selectedIds.has(loc.id);
                const networkMatch = loc.notes?.match(/Rede\s+(PRIO|Shell|Repsol|BP|Galp|Cepsa)/i);
                return (
                  <Box
                    key={loc.id}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '40px 32px 2.1fr 2.2fr 0.95fr 1fr 110px 108px',
                      alignItems: 'center',
                      padding: '10px 16px',
                      borderBottom: '1px solid #f1f5f9',
                      background: isSelected ? 'rgba(99,102,241,0.06)' : (idx % 2 === 0 ? '#fff' : '#fcfcfd'),
                      transition: 'background 0.12s',
                      cursor: 'default',
                      position: 'relative',
                    }}
                    onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.background = '#f8fafc'; }}
                    onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.background = idx % 2 === 0 ? '#fff' : '#fcfcfd'; }}
                  >
                    {/* Left accent bar when selected */}
                    {isSelected && (
                      <Box style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 3, background: tp.color }} />
                    )}

                    {/* Checkbox */}
                    <Checkbox size="small" checked={isSelected}
                      onChange={() => toggleSelect(loc.id)}
                      style={{ padding: 2, color: isSelected ? tp.color : '#cbd5e1' }} />

                    {/* Preferred star (clickable toggle) */}
                    <Tooltip title={loc.isPreferred ? (isPt ? 'Remover dos preferidos' : 'Unpin') : (isPt ? 'Marcar preferido' : 'Pin as preferred')}>
                      <IconButton size="small"
                        onClick={async () => {
                          try {
                            await dispatch(updateLocation({ token, id: loc.id, data: { ...loc, isPreferred: !loc.isPreferred } })).unwrap();
                          } catch {}
                        }}
                        style={{ padding: 3, color: loc.isPreferred ? '#eab308' : '#cbd5e1' }}>
                        {loc.isPreferred ? <StarIcon style={{ fontSize: 16 }} /> : <StarBorderIcon style={{ fontSize: 16 }} />}
                      </IconButton>
                    </Tooltip>

                    {/* Name + type icon */}
                    <Box style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                      <Box style={{
                        width: 28, height: 28, borderRadius: 7, background: tp.bg,
                        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                      }}>
                        <TIcon style={{ fontSize: 14, color: tp.color }} />
                      </Box>
                      <Box style={{ minWidth: 0 }}>
                        <Box style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <Typography style={{
                            fontSize: '0.78rem', fontWeight: 700, color: '#0f172a',
                            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                          }}>
                            {loc.name.replace(/^(PRIO|Shell|Repsol)\s*-\s*/i, '')}
                          </Typography>
                          {loc.isDeparture && (
                            <Tooltip title={isPt ? 'Local de partida' : 'Departure'}>
                              <Chip size="small" label="⛽ Partida"
                                style={{
                                  height: 16, fontSize: '0.54rem', fontWeight: 700, borderRadius: 4,
                                  background: '#ffedd5', color: '#9a3412', border: '1px solid #fed7aa',
                                }} />
                            </Tooltip>
                          )}
                        </Box>
                        {networkMatch && (
                          <Chip size="small" label={networkMatch[1].toUpperCase()}
                            style={{
                              height: 14, fontSize: '0.52rem', fontWeight: 800, borderRadius: 3, marginTop: 2,
                              background: networkMatch[1].match(/prio/i) ? '#dcfce7'
                                : networkMatch[1].match(/shell|galp/i) ? '#fef3c7'
                                : '#fee2e2',
                              color: networkMatch[1].match(/prio/i) ? '#16a34a'
                                : networkMatch[1].match(/shell|galp/i) ? '#ca8a04'
                                : '#dc2626',
                            }} />
                        )}
                      </Box>
                    </Box>

                    {/* Address */}
                    <Typography style={{
                      fontSize: '0.72rem', color: '#475569',
                      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                    }}>
                      {loc.address || <span style={{ color: '#cbd5e1', fontStyle: 'italic' }}>—</span>}
                    </Typography>

                    {/* Type chip */}
                    <Box>
                      <Chip size="small"
                        label={t(lang, tp.labelKey)}
                        style={{
                          height: 20, fontSize: '0.62rem', fontWeight: 700,
                          background: tp.bg, color: tp.color, border: `1px solid ${tp.color}20`,
                        }}
                      />
                    </Box>

                    {/* Category (coluna própria) */}
                    <Box style={{ minWidth: 0 }}>
                      {loc.category ? (
                        <Box style={{
                          display: 'inline-flex', alignItems: 'center', gap: 6, maxWidth: '100%',
                          height: 22, padding: '0 10px 0 8px', borderRadius: 999,
                          background: `${loc.category.color}14`, border: `1px solid ${loc.category.color}40`,
                        }}>
                          <Box style={{ width: 8, height: 8, borderRadius: '50%', background: loc.category.color, flexShrink: 0 }} />
                          <Typography style={{
                            fontSize: '0.64rem', fontWeight: 700, color: loc.category.color,
                            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                          }}>{loc.category.name}</Typography>
                        </Box>
                      ) : (
                        <Typography style={{ fontSize: '0.64rem', color: '#cbd5e1' }}>—</Typography>
                      )}
                      {loc.obra && (
                        <Tooltip title={isPt ? `Ligada à obra ${loc.obra.codigoObra}` : `Linked to work ${loc.obra.codigoObra}`}>
                          <Box style={{
                            display: 'inline-flex', alignItems: 'center', gap: 4, marginTop: 3, maxWidth: '100%',
                            height: 20, padding: '0 8px', borderRadius: 999,
                            background: '#fef3c7', border: '1px solid #fcd34d',
                          }}>
                            <HomeWorkIcon style={{ fontSize: 11, color: '#92400e', flexShrink: 0 }} />
                            <Typography style={{ fontSize: '0.62rem', fontWeight: 700, color: '#92400e', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {isPt ? 'Obra' : 'Work'} {loc.obra.codigoObra}
                            </Typography>
                          </Box>
                        </Tooltip>
                      )}
                    </Box>

                    {/* Coordinates */}
                    <Typography style={{
                      fontSize: '0.64rem', color: '#94a3b8', fontFamily: 'monospace',
                      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                    }}>
                      {loc.latitude && loc.longitude
                        ? `${Number(loc.latitude).toFixed(4)}, ${Number(loc.longitude).toFixed(4)}`
                        : '—'}
                    </Typography>

                    {/* Actions */}
                    <Box style={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                      <Tooltip title={isPt ? 'Ver no mapa' : 'Show on map'}>
                        <IconButton size="small" onClick={() => {
                          window.dispatchEvent(new CustomEvent('showLocationsOnMap', { detail: [loc] }));
                          window.dispatchEvent(new CustomEvent('mapZoomTo', { detail: { lat: Number(loc.latitude), lng: Number(loc.longitude), name: loc.name } }));
                          dispatch(navigateTo('planner'));
                          if (onClose) onClose();
                        }} style={{ padding: 5, color: '#22c55e' }}>
                          <PlaceIcon style={{ fontSize: 15 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={isPt ? 'Editar' : 'Edit'}>
                        <IconButton size="small" onClick={() => startEdit(loc)}
                          style={{ padding: 5, color: '#6366f1' }}>
                          <EditIcon style={{ fontSize: 14 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={isPt ? 'Eliminar' : 'Delete'}>
                        <IconButton size="small" onClick={() => setDeleteConfirm(loc)}
                          style={{ padding: 5, color: '#ef4444' }}>
                          <DeleteIcon style={{ fontSize: 14 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Box>
        )}
      </Box>

      {/* Bulk action floating bar — appears when rows selected */}
      {selectedIds.size > 0 && (
        <Box style={{
          position: 'absolute', bottom: 20, left: '50%', transform: 'translateX(-50%)',
          zIndex: 5, display: 'flex', alignItems: 'center', gap: 10,
          padding: '8px 8px 8px 16px', borderRadius: 28,
          background: '#0f172a', color: '#fff',
          boxShadow: '0 10px 30px rgba(15,23,42,0.3)',
        }}>
          <Typography style={{ fontSize: '0.76rem', fontWeight: 700 }}>
            {selectedIds.size} {isPt ? (selectedIds.size === 1 ? 'selecionado' : 'selecionados') : (selectedIds.size === 1 ? 'selected' : 'selected')}
          </Typography>
          <Button size="small"
            startIcon={<PlaceIcon style={{ fontSize: 15 }} />}
            onClick={handleShowOnMap}
            style={{
              fontSize: '0.72rem', textTransform: 'none', fontWeight: 700,
              background: '#22c55e', color: '#fff', borderRadius: 20, padding: '5px 14px',
            }}>
            {isPt ? 'Ver no mapa' : 'Show on map'}
          </Button>
          <Button size="small"
            onClick={() => setSelectedIds(new Set())}
            style={{
              fontSize: '0.72rem', textTransform: 'none', fontWeight: 600,
              color: '#cbd5e1', borderRadius: 20, padding: '5px 10px',
            }}>
            {isPt ? 'Cancelar' : 'Clear'}
          </Button>
        </Box>
      )}

      {/* Form overlay */}
      {showForm && (
        <Box className={classes.formOverlay}>
          <Box className={classes.formHeader}>
            <Box style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <Box style={{
                width: 38, height: 38, borderRadius: 10,
                background: editId ? 'linear-gradient(135deg, #f59e0b, #eab308)' : 'linear-gradient(135deg, #3b82f6, #2563eb)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: editId ? '0 2px 8px rgba(245,158,11,0.3)' : '0 2px 8px rgba(59,130,246,0.3)',
              }}>
                {editId ? <EditIcon style={{ fontSize: 18, color: '#fff' }} /> : <AddIcon style={{ fontSize: 18, color: '#fff' }} />}
              </Box>
              <Box>
                <Typography style={{ fontSize: '0.95rem', fontWeight: 700, color: '#1e40af' }}>
                  {editId ? (isPt ? 'Editar Local' : 'Edit Location') : (isPt ? 'Novo Local' : 'New Location')}
                </Typography>
                <Typography style={{ fontSize: '0.65rem', color: '#64748b' }}>
                  {isPt ? 'Preencha os dados do local' : 'Fill in the location details'}
                </Typography>
              </Box>
            </Box>
            <IconButton size="small" onClick={resetForm} style={{ color: '#64748b', background: 'rgba(0,0,0,0.04)', width: 32, height: 32 }}>
              <CloseIcon style={{ fontSize: 18 }} />
            </IconButton>
          </Box>

          <Box className={classes.formBody}>
            {/* Type selection */}
            <Typography className={classes.formLabel} style={{ marginTop: 0 }}>{t(lang, 'type')}</Typography>
            <Box className={classes.typeGrid}>
              {TYPES.map(tp => {
                const TIcon = tp.icon;
                const active = form.type === tp.value;
                return (
                  <Box key={tp.value} onClick={() => upd('type', tp.value)}
                    className={classes.typeBtn}
                    style={{
                      background: active ? tp.bg : '#fff',
                      borderColor: active ? tp.color : 'transparent',
                      boxShadow: active ? `0 4px 12px ${tp.color}30` : '0 1px 3px rgba(0,0,0,0.04)',
                      transform: active ? 'translateY(-2px)' : 'none',
                    }}>
                    <Box style={{
                      width: 44, height: 44, borderRadius: 12,
                      background: active ? tp.color : tp.bg,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      transition: 'all 0.2s',
                    }}>
                      <TIcon style={{ fontSize: 22, color: active ? '#fff' : tp.color }} />
                    </Box>
                    <Typography style={{
                      fontSize: '0.62rem', fontWeight: active ? 700 : 600,
                      color: active ? tp.color : '#475569', textAlign: 'center',
                      lineHeight: 1.2,
                    }}>
                      {t(lang, tp.labelKey)}
                    </Typography>
                  </Box>
                );
              })}
            </Box>

            {/* ── Categoria do cliente (opcional) — cartão dedicado ──────────
                Dimensão SEPARADA do tipo funcional. Criação inline (sem sair
                do form) + gestão completa no "Gerir". */}
            <Box style={{
              marginTop: 16, borderRadius: 12, border: '1px solid #e8edf3',
              background: 'linear-gradient(180deg, #fbfcff 0%, #f8fafc 100%)', padding: '12px 14px',
            }}>
              <Box style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                <Box style={{ width: 24, height: 24, borderRadius: 7, background: '#eef2ff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <LocalOfferIcon style={{ fontSize: 14, color: '#6366f1' }} />
                </Box>
                <Box style={{ flex: 1, minWidth: 0 }}>
                  <Typography style={{ fontSize: '0.78rem', fontWeight: 700, color: '#0f172a', lineHeight: 1.1 }}>
                    {isPt ? 'Categoria' : 'Category'} <span style={{ color: '#94a3b8', fontWeight: 500, fontSize: '0.68rem' }}>· {isPt ? 'opcional' : 'optional'}</span>
                  </Typography>
                  <Typography style={{ fontSize: '0.64rem', color: '#94a3b8', marginTop: 1 }}>
                    {isPt ? 'A tua etiqueta própria (ex.: Obra). Não altera o cálculo de rotas.' : 'Your own label. Does not affect routing.'}
                  </Typography>
                </Box>
                {categories.length > 0 && (
                  <Button size="small" startIcon={<SettingsIcon style={{ fontSize: 13 }} />}
                    onClick={() => setShowCatManager(true)}
                    style={{ textTransform: 'none', fontSize: '0.66rem', color: '#6366f1', fontWeight: 700, flexShrink: 0 }}>
                    {isPt ? 'Gerir' : 'Manage'}
                  </Button>
                )}
              </Box>

              <Box style={{ display: 'flex', flexWrap: 'wrap', gap: 7, alignItems: 'center' }}>
                {/* Sem categoria */}
                <Box onClick={() => upd('categoryId', '')}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer',
                    height: 30, padding: '0 12px', borderRadius: 999,
                    background: !form.categoryId ? '#e2e8f0' : '#fff',
                    border: `1.5px solid ${!form.categoryId ? '#94a3b8' : '#e2e8f0'}`,
                    transition: 'all 0.12s',
                  }}>
                  {!form.categoryId && <CheckIcon style={{ fontSize: 14, color: '#475569' }} />}
                  <Typography style={{ fontSize: '0.72rem', fontWeight: 600, color: '#475569' }}>{isPt ? 'Sem categoria' : 'None'}</Typography>
                </Box>

                {/* Categorias existentes */}
                {categories.map(c => {
                  const active = Number(form.categoryId) === c.id;
                  return (
                    <Box key={c.id} onClick={() => upd('categoryId', c.id)}
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer',
                        height: 30, padding: '0 12px', borderRadius: 999,
                        background: active ? `${c.color}1f` : '#fff',
                        border: `1.5px solid ${active ? c.color : '#e2e8f0'}`,
                        boxShadow: active ? `0 2px 8px ${c.color}26` : 'none',
                        transition: 'all 0.12s',
                      }}>
                      {active
                        ? <CheckIcon style={{ fontSize: 14, color: c.color }} />
                        : <Box style={{ width: 9, height: 9, borderRadius: '50%', background: c.color }} />}
                      <Typography style={{ fontSize: '0.72rem', fontWeight: 700, color: active ? c.color : '#334155', whiteSpace: 'nowrap' }}>{c.name}</Typography>
                    </Box>
                  );
                })}

                {/* + Nova categoria (toggle do criador inline) */}
                {!inlineCat.show && (
                  <Box onClick={() => setInlineCat(s => ({ ...s, show: true }))}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 5, cursor: 'pointer',
                      height: 30, padding: '0 12px', borderRadius: 999,
                      background: '#fff', border: '1.5px dashed #c7d2fe', color: '#6366f1',
                    }}>
                    <AddIcon style={{ fontSize: 15 }} />
                    <Typography style={{ fontSize: '0.72rem', fontWeight: 700, color: '#6366f1' }}>{isPt ? 'Nova categoria' : 'New category'}</Typography>
                  </Box>
                )}
              </Box>

              {/* Criador inline */}
              {inlineCat.show && (
                <Box style={{ marginTop: 10, padding: '10px 12px', borderRadius: 10, background: '#fff', border: '1px solid #e2e8f0' }}>
                  <Box style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input type="color" value={inlineCat.color}
                      onChange={e => setInlineCat(s => ({ ...s, color: e.target.value }))}
                      title={isPt ? 'Cor' : 'Colour'}
                      style={{ width: 30, height: 30, border: 'none', background: 'none', padding: 0, cursor: 'pointer', flexShrink: 0 }} />
                    <TextField variant="outlined" size="small" fullWidth autoFocus
                      placeholder={isPt ? 'Nome da categoria (ex.: Obra)' : 'Category name'}
                      value={inlineCat.name}
                      onChange={e => setInlineCat(s => ({ ...s, name: e.target.value }))}
                      onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); createInlineCategory(); } if (e.key === 'Escape') setInlineCat({ show: false, name: '', color: CAT_PALETTE[0] }); }}
                      InputProps={{ style: { fontSize: '0.8rem' } }} />
                    <Button variant="contained" disableElevation disabled={!inlineCat.name.trim()} onClick={createInlineCategory}
                      style={{ background: inlineCat.name.trim() ? '#4f46e5' : '#cbd5e1', color: '#fff', textTransform: 'none', fontWeight: 700, whiteSpace: 'nowrap' }}>
                      {isPt ? 'Criar' : 'Add'}
                    </Button>
                    <IconButton size="small" onClick={() => setInlineCat({ show: false, name: '', color: CAT_PALETTE[0] })}>
                      <CloseIcon style={{ fontSize: 16 }} />
                    </IconButton>
                  </Box>
                  <Box style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                    {CAT_PALETTE.map(col => (
                      <Box key={col} onClick={() => setInlineCat(s => ({ ...s, color: col }))}
                        style={{ width: 18, height: 18, borderRadius: '50%', background: col, cursor: 'pointer',
                          border: inlineCat.color === col ? '2px solid #0f172a' : '2px solid transparent' }} />
                    ))}
                  </Box>
                </Box>
              )}
            </Box>

            {/* Ligação a Obra — só quando a CATEGORIA selecionada é "Obra".
                Escolher uma obra existente OU criar uma a partir deste favorito
                (pede o código). As coordenadas sincronizam (último a gravar manda). */}
            {(() => {
              const selCat = categories.find(c => Number(c.id) === Number(form.categoryId));
              const isObraCat = !!selCat && /^obra$/i.test((selCat.name || '').trim());
              if (!isObraCat) return null;
              const linkedObra = obrasList.find(o => Number(o.id) === Number(form.obraId)) || null;
              return (
                <Box style={{ marginTop: 14, padding: '12px 14px', borderRadius: 12, background: '#fffbeb', border: '1px solid #fde68a' }}>
                  <Typography className={classes.formLabel} style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#92400e' }}>
                    <HomeWorkIcon style={{ fontSize: 15, color: '#f59e0b' }} /> {isPt ? 'Obra associada' : 'Linked work'}
                  </Typography>

                  {linkedObra ? (
                    <Box style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
                      <Box style={{ display: 'inline-flex', alignItems: 'center', gap: 5, height: 24, padding: '0 10px', borderRadius: 999, background: '#fef3c7', border: '1px solid #fcd34d' }}>
                        <Typography style={{ fontSize: '0.72rem', fontWeight: 700, color: '#92400e' }}>
                          {linkedObra.codigoObra}{linkedObra.name ? ` · ${linkedObra.name}` : ''}
                        </Typography>
                      </Box>
                      <Button size="small" onClick={() => upd('obraId', '')} style={{ textTransform: 'none', fontSize: '0.72rem', color: '#b91c1c' }}>
                        {isPt ? 'Desligar' : 'Unlink'}
                      </Button>
                    </Box>
                  ) : obraCreate.show ? (
                    <Box style={{ marginTop: 8 }}>
                      {obraCreate.err && <Typography style={{ fontSize: '0.68rem', color: '#b91c1c', marginBottom: 4 }}>{obraCreate.err}</Typography>}
                      <Box style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <TextField variant="outlined" size="small" autoFocus
                          placeholder={isPt ? 'Código da obra (ex.: 903403)' : 'Work code'}
                          value={obraCreate.codigo}
                          onChange={e => setObraCreate(s => ({ ...s, codigo: e.target.value, err: '' }))}
                          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); createObraFromFavorite(); } }}
                          style={{ flex: 1 }} InputProps={{ style: { fontSize: '0.8rem' } }} />
                        <Button variant="contained" disableElevation disabled={obraCreate.saving || !obraCreate.codigo.trim()}
                          onClick={createObraFromFavorite}
                          style={{ background: obraCreate.codigo.trim() ? '#f59e0b' : '#cbd5e1', color: '#fff', textTransform: 'none', fontWeight: 700, whiteSpace: 'nowrap' }}>
                          {obraCreate.saving ? '…' : (isPt ? 'Criar obra' : 'Create')}
                        </Button>
                        <IconButton size="small" onClick={() => setObraCreate({ show: false, codigo: '', saving: false, err: '' })}>
                          <CloseIcon style={{ fontSize: 16 }} />
                        </IconButton>
                      </Box>
                      <Typography style={{ fontSize: '0.64rem', color: '#a16207', marginTop: 4 }}>
                        {isPt ? 'Nome e coordenadas vêm deste favorito.' : 'Name and coordinates come from this favorite.'}
                      </Typography>
                    </Box>
                  ) : (
                    <Box style={{ marginTop: 8 }}>
                      <Autocomplete
                        size="small"
                        options={obrasList}
                        value={null}
                        getOptionLabel={(o) => o ? `${o.codigoObra}${o.name ? ` · ${o.name}` : ''}` : ''}
                        getOptionSelected={(o, v) => o.id === v.id}
                        onChange={(e, v) => { upd('obraId', v ? v.id : ''); if (v) upd('type', 'other'); }}
                        renderInput={(params) => (
                          <TextField {...params} variant="outlined"
                            placeholder={isPt ? 'Escolher obra existente…' : 'Pick existing work…'} />
                        )}
                      />
                      <Button size="small" startIcon={<AddIcon style={{ fontSize: 15 }} />}
                        onClick={() => setObraCreate({ show: true, codigo: '', saving: false, err: '' })}
                        style={{ textTransform: 'none', fontSize: '0.72rem', color: '#b45309', fontWeight: 700, marginTop: 4 }}>
                        {isPt ? 'Ou criar obra a partir deste favorito' : 'Or create a work from this favorite'}
                      </Button>
                    </Box>
                  )}
                </Box>
              );
            })()}

            {/* Name + Address — 2 columns */}
            <Box style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginTop: 16 }}>
              <Box>
                <Typography className={classes.formLabel}>{t(lang, 'name')} *</Typography>
                <TextField className={classes.formInput} variant="outlined" size="small" fullWidth
                  placeholder={isPt ? 'Ex: Armazém Lisboa' : 'Ex: Warehouse Lisbon'}
                  value={form.name} onChange={e => { upd('name', e.target.value); setError(''); }} />
              </Box>
              <Box style={{ position: 'relative' }}>
                <Typography className={classes.formLabel}>{t(lang, 'locationAddress')}</Typography>
                <TextField className={classes.formInput} variant="outlined" size="small" fullWidth
                  placeholder={isPt ? 'Ex: Rua da Indústria 45, Lisboa' : 'Ex: 45 Industry St'}
                  value={form.address}
                  onFocus={() => setAddrFocused(true)}
                  onBlur={() => setTimeout(() => setAddrFocused(false), 200)}
                  onChange={e => {
                    const val = e.target.value;
                    upd('address', val);
                    // Geocoding pormenorizado: procura à medida que se
                    // escreve (debounce 350ms). Picking uma sugestão
                    // preenche morada + lat/lng exactas.
                    if (addrDebounceRef.current) clearTimeout(addrDebounceRef.current);
                    addrDebounceRef.current = setTimeout(async () => {
                      if (val.trim().length >= 3) {
                        try {
                          const results = await geocodeSearchMulti(val, {
                            country: clientSettings?.defaultCountry || 'PT',
                          });
                          setAddrSuggestions(results || []);
                        } catch { setAddrSuggestions([]); }
                      } else {
                        setAddrSuggestions([]);
                      }
                    }, 350);
                  }} />
                {addrFocused && addrSuggestions.length > 0 && (
                  <Box style={{
                    position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 30,
                    background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8,
                    boxShadow: '0 8px 24px rgba(15,23,42,0.18)', maxHeight: 240,
                    overflowY: 'auto', marginTop: 2,
                  }}>
                    {addrSuggestions.map((s, i) => {
                      const srcCfg = ({
                        'google-geo': { label: 'Morada', color: '#15803d', bg: 'rgba(22,163,74,0.12)' },
                        google: { label: 'Places', color: '#1a73e8', bg: 'rgba(26,115,232,0.10)' },
                        mapbox: { label: 'Mapbox', color: '#475569', bg: 'rgba(71,85,105,0.10)' },
                        here:   { label: 'HERE',   color: '#0ea5e9', bg: 'rgba(14,165,233,0.10)' },
                        osm:    { label: 'OSM',    color: '#16a34a', bg: 'rgba(22,163,74,0.10)' },
                      })[s.source] || null;
                      return (
                        <Box key={i}
                          onMouseDown={() => {
                            upd('address', s.name);
                            if (Array.isArray(s.coordinates) && s.coordinates.length === 2) {
                              upd('longitude', String(s.coordinates[0]));
                              upd('latitude', String(s.coordinates[1]));
                            }
                            setAddrSuggestions([]);
                          }}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 6,
                            padding: '7px 10px', cursor: 'pointer',
                            borderBottom: '1px solid #f1f5f9', fontSize: '0.74rem',
                          }}
                          onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                          onMouseLeave={e => e.currentTarget.style.background = '#fff'}>
                          <PlaceIcon style={{ fontSize: 14, color: '#94a3b8', flexShrink: 0 }} />
                          <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#0f172a' }}>
                            {s.name}
                          </span>
                          {srcCfg && (
                            <span style={{ fontSize: '0.56rem', fontWeight: 700, color: srcCfg.color, background: srcCfg.bg, padding: '1px 5px', borderRadius: 3, flexShrink: 0 }}>
                              {srcCfg.label}
                            </span>
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                )}
              </Box>
            </Box>

            {/* Coordinates + Pick from map */}
            <Typography className={classes.formLabel}>{t(lang, 'locationCoords')} *</Typography>
            <Box className={classes.coordRow}>
              <TextField className={classes.formInput} variant="outlined" size="small"
                placeholder="Latitude" type="number" value={form.latitude}
                onChange={e => upd('latitude', e.target.value)} style={{ flex: 1 }} />
              <TextField className={classes.formInput} variant="outlined" size="small"
                placeholder="Longitude" type="number" value={form.longitude}
                onChange={e => upd('longitude', e.target.value)} style={{ flex: 1 }} />
              <Tooltip title={isPt ? 'Marcar no mapa' : 'Pick from map'}>
                <Button size="small" variant="outlined"
                  onClick={() => {
                    setPickingFromMap(true);
                    if (onPickFromMap) onPickFromMap(handleMapPick);
                  }}
                  style={{
                    minWidth: 36, height: 36, borderRadius: 8, padding: 0,
                    borderColor: pickingFromMap ? '#3b82f6' : '#e2e8f0',
                    background: pickingFromMap ? '#eff6ff' : '#fff',
                  }}>
                  <MyLocationIcon style={{ fontSize: 18, color: pickingFromMap ? '#3b82f6' : '#64748b' }} />
                </Button>
              </Tooltip>
            </Box>
            {pickingFromMap && (
              <Box className={classes.pickMapBanner} style={{ marginTop: 8, borderRadius: 8, padding: '8px 12px' }}>
                <MyLocationIcon style={{ fontSize: 16, color: '#3b82f6' }} />
                <Typography style={{ fontSize: '0.7rem', color: '#1d4ed8', fontWeight: 600 }}>
                  {isPt ? 'Clique no mapa para seleccionar as coordenadas' : 'Click on the map to select coordinates'}
                </Typography>
              </Box>
            )}

            {/* Notes */}
            <Typography className={classes.formLabel}>{t(lang, 'locationNotes')}</Typography>
            <TextField className={classes.formInput} variant="outlined" size="small" fullWidth multiline rows={2}
              placeholder={isPt ? 'Notas opcionais...' : 'Optional notes...'}
              value={form.notes} onChange={e => upd('notes', e.target.value)} />

            {/* Preferred toggle */}
            <Tooltip
              placement="left"
              arrow
              enterDelay={300}
              title={
                <Box style={{ padding: 4, maxWidth: 300 }}>
                  <Typography style={{ fontSize: '0.78rem', fontWeight: 700, color: '#fbbf24', marginBottom: 4 }}>
                    ⭐ Preferencial
                  </Typography>
                  <Typography style={{ fontSize: '0.72rem', lineHeight: 1.45, color: '#fff' }}>
                    Marca o favorito como <b>"top-of-list"</b> nos selectors.<br /><br />
                    <b>Onde tem efeito:</b><br />
                    • Botão ⭐ no planeamento (origem/destino/paragem) — mostra só os preferidos no popover<br />
                    • Lista de favoritos no formulário de OT — preferidos aparecem em cima<br /><br />
                    <b>Não afecta o cálculo da rota</b> — é puramente atalho visual para os locais usados frequentemente.
                  </Typography>
                </Box>
              }
            >
              <Box onClick={() => upd('isPreferred', !form.isPreferred)} className={classes.prefRow}
                style={{
                  background: form.isPreferred ? '#fef9c3' : '#fafbfc',
                  border: `2px solid ${form.isPreferred ? '#eab308' : '#e2e8f0'}`,
                  cursor: 'pointer',
                }}>
                {form.isPreferred
                  ? <StarIcon style={{ fontSize: 20, color: '#eab308' }} />
                  : <StarBorderIcon style={{ fontSize: 20, color: '#94a3b8' }} />}
                <Box style={{ flex: 1 }}>
                  <Typography style={{ fontSize: '0.76rem', fontWeight: 600, color: form.isPreferred ? '#92400e' : '#64748b' }}>
                    {t(lang, 'preferred')}
                  </Typography>
                  <Typography style={{ fontSize: '0.58rem', color: '#94a3b8' }}>
                    {isPt
                      ? 'Aparece no topo das listas — para os locais que usas mais vezes'
                      : 'Pinned to the top of the lists — for the places you use most'}
                  </Typography>
                </Box>
              </Box>
            </Tooltip>

            {/* Include in route calculation toggle */}
            <Tooltip
              placement="left"
              arrow
              enterDelay={300}
              title={
                <Box style={{ padding: 4, maxWidth: 300 }}>
                  <Typography style={{ fontSize: '0.78rem', fontWeight: 700, color: '#60a5fa', marginBottom: 4 }}>
                    🛣 Incluir no cálculo da rota
                  </Typography>
                  <Typography style={{ fontSize: '0.72rem', lineHeight: 1.45, color: '#fff' }}>
                    Marca o favorito como <b>candidato a paragem do tacógrafo</b> em rotas com motorista (Reg. EU 561/2006).<br /><br />
                    <b>Como funciona:</b><br />
                    • O planner busca áreas de descanso reais (OSM) ao longo da rota<br />
                    • Soma os favoritos com este flag à mesma lista<br />
                    • O solver escolhe onde parar quando o motorista atinge limite de condução<br /><br />
                    <b>Tipos elegíveis:</b> Área Descanso, Pernoita, Combustível, Ponto Seguro. Outros tipos são ignorados mesmo com o flag activo.<br /><br />
                    <b>Tem efeito directo</b> no cálculo — pode mudar onde o camião pára à noite.
                  </Typography>
                </Box>
              }
            >
              <Box onClick={() => upd('includeInRoute', !form.includeInRoute)} className={classes.prefRow}
                style={{
                  background: form.includeInRoute ? '#dbeafe' : '#fafbfc',
                  border: `2px solid ${form.includeInRoute ? '#3b82f6' : '#e2e8f0'}`,
                  marginTop: 8,
                  cursor: 'pointer',
                }}>
                {form.includeInRoute
                  ? <PlaceIcon style={{ fontSize: 20, color: '#3b82f6' }} />
                  : <PlaceIcon style={{ fontSize: 20, color: '#94a3b8' }} />}
                <Box style={{ flex: 1 }}>
                  <Typography style={{ fontSize: '0.76rem', fontWeight: 600, color: form.includeInRoute ? '#1e40af' : '#64748b' }}>
                    {isPt ? 'Incluir no cálculo da rota' : 'Include in route calculation'}
                  </Typography>
                  <Typography style={{ fontSize: '0.58rem', color: '#94a3b8' }}>
                    {isPt
                      ? 'Pode ser usado como paragem de descanso do motorista durante a rota'
                      : 'Can be used as a driver rest stop during the route'}
                  </Typography>
                </Box>
              </Box>
            </Tooltip>

            {/* Local de partida — qualquer ponto onde os veículos podem
                começar uma rota: parque dos camiões, armazém, ou casa
                de um motorista. Múltiplos permitidos.
                Tooltip posicionada em cima porque esta é a última opção
                do formulário — com `placement="left"` saía pelo fundo
                do ecrã e o operador não conseguia ler até ao fim. */}
            <Tooltip
              placement="top"
              arrow
              enterDelay={300}
              title={
                <Box style={{ padding: 4, maxWidth: 300 }}>
                  <Typography style={{ fontSize: '0.78rem', fontWeight: 700, color: '#fb923c', marginBottom: 4 }}>
                    🏁 Local de partida
                  </Typography>
                  <Typography style={{ fontSize: '0.72rem', lineHeight: 1.45, color: '#fff' }}>
                    Marca este favorito como um <b>ponto de onde os veículos podem arrancar</b> — o parque dos camiões, um armazém, ou mesmo <b>a casa de um motorista</b>.<br /><br />
                    <b>Quando é útil:</b><br />
                    • Aparece como opção no selector "Local de Partida" ao atribuir veículo a uma rota<br />
                    • Rotas de combustível começam aqui automaticamente (partida → recolha → cliente)<br /><br />
                    <b>Podes marcar vários:</b> ex. um parque em Lisboa, outro no Porto, e a casa de cada motorista. Em cada rota escolhes qual usar.<br /><br />
                    <b>Atalho:</b> se só existir <b>1</b> local de partida, é escolhido automaticamente nas rotas de combustível.
                  </Typography>
                </Box>
              }
            >
              <Box onClick={() => upd('isDeparture', !form.isDeparture)} className={classes.prefRow}
                style={{
                  background: form.isDeparture ? '#ffedd5' : '#fafbfc',
                  border: `2px solid ${form.isDeparture ? '#f97316' : '#e2e8f0'}`,
                  marginTop: 8,
                  cursor: 'pointer',
                }}>
                <Box style={{
                  width: 20, height: 20, borderRadius: 4,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 16,
                }}>⛽</Box>
                <Box style={{ flex: 1 }}>
                  <Typography style={{ fontSize: '0.76rem', fontWeight: 600, color: form.isDeparture ? '#9a3412' : '#64748b' }}>
                    {isPt ? 'Local de partida' : 'Departure'}
                  </Typography>
                  <Typography style={{ fontSize: '0.58rem', color: '#94a3b8' }}>
                    {isPt
                      ? 'Parque, armazém ou casa do motorista — onde a rota arranca'
                      : 'Yard, depot or driver’s home — where the route starts'}
                  </Typography>
                </Box>
              </Box>
            </Tooltip>

            {error && (
              <Box style={{ marginTop: 12, padding: '8px 12px', background: '#fef2f2', borderRadius: 8, border: '1px solid #fecaca' }}>
                <Typography style={{ color: '#dc2626', fontSize: '0.72rem', fontWeight: 600 }}>{error}</Typography>
              </Box>
            )}
          </Box>

          <Box className={classes.formActions}>
            <Button size="small" onClick={resetForm}
              style={{ fontSize: '0.72rem', color: '#64748b', textTransform: 'none', fontWeight: 600, padding: '6px 16px' }}>
              {t(lang, 'cancel')}
            </Button>
            <Button size="small" variant="contained" onClick={handleSave}
              style={{
                fontSize: '0.72rem', color: '#fff', borderRadius: 8, textTransform: 'none', fontWeight: 600,
                padding: '6px 24px', boxShadow: 'none',
                background: editId ? 'linear-gradient(135deg, #f59e0b, #eab308)' : 'linear-gradient(135deg, #3b82f6, #6366f1)',
              }}>
              {editId ? t(lang, 'save') : (isPt ? 'Adicionar' : 'Add')}
            </Button>
          </Box>
        </Box>
      )}

      {/* Delete confirmation */}
      {deleteConfirm && (
        <Box className={classes.deleteOverlay}>
          <Box style={{
            background: '#fff', borderRadius: 16, padding: '28px 32px', width: 360, maxWidth: '90%',
            boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
          }}>
            <Box style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
              <Box style={{ width: 40, height: 40, borderRadius: 10, background: '#fef2f2', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <DeleteIcon style={{ fontSize: 20, color: '#ef4444' }} />
              </Box>
              <Typography style={{ fontSize: '0.95rem', fontWeight: 700, color: '#0f172a' }}>
                {isPt ? 'Eliminar Local' : 'Delete Location'}
              </Typography>
            </Box>
            <Typography style={{ fontSize: '0.78rem', color: '#64748b', marginBottom: 8 }}>
              {isPt ? 'Tem a certeza que pretende eliminar:' : 'Are you sure you want to delete:'}
            </Typography>
            <Box style={{ padding: '12px 16px', background: '#f8fafc', borderRadius: 10, border: '1px solid #e2e8f0', marginBottom: 20 }}>
              <Typography style={{ fontSize: '0.82rem', fontWeight: 700, color: '#0f172a' }}>{deleteConfirm.name}</Typography>
              {deleteConfirm.address && <Typography style={{ fontSize: '0.65rem', color: '#94a3b8', marginTop: 2 }}>{deleteConfirm.address}</Typography>}
            </Box>
            <Box style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <Button size="small" onClick={() => setDeleteConfirm(null)}
                style={{ fontSize: '0.72rem', color: '#64748b', textTransform: 'none', fontWeight: 600, padding: '6px 16px' }}>
                {t(lang, 'cancel')}
              </Button>
              <Button size="small" variant="contained"
                onClick={async () => { await handleDelete(deleteConfirm.id); setDeleteConfirm(null); }}
                style={{ fontSize: '0.72rem', background: '#ef4444', color: '#fff', borderRadius: 8, textTransform: 'none', fontWeight: 600, padding: '6px 20px', boxShadow: 'none' }}>
                {isPt ? 'Eliminar' : 'Delete'}
              </Button>
            </Box>
          </Box>
        </Box>
      )}

      {/* Gestor de categorias de locais */}
      <CategoryManagerDialog
        open={showCatManager}
        onClose={() => setShowCatManager(false)}
        token={token}
        categories={categories}
        isPt={isPt}
        onChanged={async () => { await fetchCategories(); if (token) dispatch(fetchLocations(token)); }}
      />
    </Box>
  );

  if (embedded) return content;

  return (
    <>
    {/* Minimized bar */}
    {open && minimized && (
      <Box style={{
        position: 'fixed', bottom: 16, left: '50%', transform: 'translateX(-50%)',
        zIndex: 1300, display: 'flex', alignItems: 'center', gap: 10,
        background: '#fff', borderRadius: 24, padding: '8px 16px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.15)', cursor: 'pointer',
      }} onClick={() => setMinimized(false)}>
        <StarIcon style={{ fontSize: 18, color: '#f59e0b' }} />
        <Typography style={{ fontSize: '0.78rem', fontWeight: 600, color: '#1e293b' }}>
          {isPt ? 'Favoritos' : 'Favourites'} ({locations.length})
        </Typography>
        {selectedIds.size > 0 && (
          <Chip size="small" label={`${selectedIds.size} ${isPt ? 'selecionado(s)' : 'selected'}`}
            style={{ height: 20, fontSize: '0.58rem', fontWeight: 700, background: '#22c55e', color: '#fff' }} />
        )}
      </Box>
    )}
    {/* Full panel — fullscreen like Orders */}
    <Box style={{
      display: (open && !minimized) ? 'flex' : 'none',
      position: 'fixed', top: 54, left: 0, right: 0, bottom: 0,
      zIndex: 1100,
      background: '#fff',
      overflow: 'hidden',
      flexDirection: 'column',
    }}>
      {content}
    </Box>
    </>
  );
}

export default ManageFavoritesDialog;
